import { c as _c } from "react/compiler-runtime";
import React, { use, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import notFoundNewConsumerApp from 'commons/notFoundNewConsumerApp';
import PDPDesktop from './PDPDesktop';
import PDPMobile from './PDPMobile';
import PDPDesktopRevamp from './PDPDesktopRevamp';
import PDPMobileRevamp from './PDPMobileRevamp';
import useIsLayoutRevampVariant from './useIsLayoutRevampVariant';
import MobileAndTabletOnly from '../MobileAndTabletOnly';
import DesktopOnly from '../DesktopOnly';
import PDPSEOTags from './PDPSEOTags';
import PDPFooter from './PDPFooter';
import PDPContext from './state/context/PDPContext';
import PDPStickyButtonsProvider from './state/context/PDPStickyButtonsProvider';
import usePDPLuxAnalytics from './hooks/usePDPLuxAnalytics';
import usePDPCreateContextValue from './hooks/usePDPCreateContextValue';
import PDPPageAnalytics from './PDPPageAnalytics';

/**
 * Options + context creation + Provider + above/below-fold tree.
 * Resolves server options/basicDetails promises via use(); hero paint stays in
 * PDPHeroShell. Zip stock / order ETA must not suspend this region.
 */
const PDPContextRegion = t0 => {
  const $ = _c(47);
  const {
    slugOverride: t1,
    isEditOrder: t2,
    pdpIsModularBYOProduct: t3,
    productOptionsPromise,
    productBasicDetailsPromise,
    productHeroImageFromServer: t4,
    productCylindoHeroImage: t5
  } = t0;
  const slugOverride = t1 === undefined ? null : t1;
  const isEditOrder = t2 === undefined ? false : t2;
  const pdpIsModularBYOProduct = t3 === undefined ? false : t3;
  const productHeroImageFromServer = t4 === undefined ? null : t4;
  const productCylindoHeroImage = t5 === undefined ? null : t5;
  const params = useParams();
  const slug = slugOverride ?? params.slug ?? null;
  const productOptions = productOptionsPromise ? use(productOptionsPromise) : null;
  const productBasicDetails = productBasicDetailsPromise ? use(productBasicDetailsPromise) : null;
  let t6;
  if ($[0] !== isEditOrder || $[1] !== pdpIsModularBYOProduct || $[2] !== productBasicDetails || $[3] !== productOptions || $[4] !== slug) {
    t6 = {
      isEditOrder,
      pdpIsModularBYOProduct,
      slug,
      productOptions,
      productBasicDetails
    };
    $[0] = isEditOrder;
    $[1] = pdpIsModularBYOProduct;
    $[2] = productBasicDetails;
    $[3] = productOptions;
    $[4] = slug;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const pdpContextValue = usePDPCreateContextValue(t6);
  usePDPLuxAnalytics();
  const isLayoutRevampVariant = useIsLayoutRevampVariant();
  const showLayoutRevamp = isLayoutRevampVariant && !pdpIsModularBYOProduct && !isEditOrder;
  let t7;
  bb0: {
    if (productHeroImageFromServer?.src) {
      t7 = productHeroImageFromServer;
      break bb0;
    }
    const heroUrl = pdpContextValue.basicDetails?.data?.hero_images?.[0]?.img?.url;
    if (!heroUrl) {
      t7 = null;
      break bb0;
    }
    const t8 = pdpContextValue.basicDetails?.data?.name ? String(pdpContextValue.basicDetails.data.name) : "";
    let t9;
    if ($[6] !== heroUrl || $[7] !== t8) {
      t9 = {
        src: heroUrl,
        width: 1600,
        height: 2400,
        alt: t8
      };
      $[6] = heroUrl;
      $[7] = t8;
      $[8] = t9;
    } else {
      t9 = $[8];
    }
    t7 = t9;
  }
  const productHeroImage = t7;
  if (pdpContextValue.basicDetails.isProductNotFound) {
    let t8;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = notFoundNewConsumerApp();
      $[9] = t8;
    } else {
      t8 = $[9];
    }
    return t8;
  }
  let t8;
  if ($[10] !== pdpContextValue.stockAvailability?.data) {
    t8 = pdpContextValue.stockAvailability?.data || {};
    $[10] = pdpContextValue.stockAvailability?.data;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const {
    discontinuedCategoryMsg: t9,
    isDiscontinued: t10,
    isOutOfStock: t11,
    showNotification
  } = t8;
  const discontinuedCategoryMsg = t9 === undefined ? null : t9;
  const isDiscontinued = t10 === undefined ? false : t10;
  const isOutOfStock = t11 === undefined ? false : t11;
  let t12;
  if ($[12] !== isOutOfStock) {
    t12 = <PDPSEOTags isOutOfStock={isOutOfStock} />;
    $[12] = isOutOfStock;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  let t13;
  if ($[14] !== discontinuedCategoryMsg || $[15] !== isDiscontinued || $[16] !== isEditOrder || $[17] !== isOutOfStock || $[18] !== pdpIsModularBYOProduct || $[19] !== productCylindoHeroImage || $[20] !== productHeroImage || $[21] !== showLayoutRevamp || $[22] !== showNotification || $[23] !== slug) {
    t13 = <MobileAndTabletOnly>{showLayoutRevamp ? <PDPMobileRevamp slug={slug} isOutOfStock={isOutOfStock} showOutOfStockNotification={showNotification} showDiscontinuedNotification={isDiscontinued} discontinuedCategoryMsg={isDiscontinued ? discontinuedCategoryMsg : null} productHeroImage={productHeroImage} productCylindoHeroImage={productCylindoHeroImage} /> : <PDPMobile slug={slug} isEditOrder={isEditOrder} isOutOfStock={isOutOfStock} showOutOfStockNotification={showNotification} pdpIsModularBYOProduct={pdpIsModularBYOProduct} showDiscontinuedNotification={isDiscontinued} discontinuedCategoryMsg={isDiscontinued ? discontinuedCategoryMsg : null} productHeroImage={productHeroImage} productCylindoHeroImage={productCylindoHeroImage} />}</MobileAndTabletOnly>;
    $[14] = discontinuedCategoryMsg;
    $[15] = isDiscontinued;
    $[16] = isEditOrder;
    $[17] = isOutOfStock;
    $[18] = pdpIsModularBYOProduct;
    $[19] = productCylindoHeroImage;
    $[20] = productHeroImage;
    $[21] = showLayoutRevamp;
    $[22] = showNotification;
    $[23] = slug;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== discontinuedCategoryMsg || $[26] !== isDiscontinued || $[27] !== isEditOrder || $[28] !== isOutOfStock || $[29] !== pdpIsModularBYOProduct || $[30] !== productCylindoHeroImage || $[31] !== productHeroImage || $[32] !== showLayoutRevamp || $[33] !== showNotification || $[34] !== slug) {
    t14 = <DesktopOnly>{showLayoutRevamp ? <PDPDesktopRevamp slug={slug} isOutOfStock={isOutOfStock} showOutOfStockNotification={showNotification} showDiscontinuedNotification={isDiscontinued} discontinuedCategoryMsg={isDiscontinued ? discontinuedCategoryMsg : null} productHeroImage={productHeroImage} productCylindoHeroImage={productCylindoHeroImage} /> : <PDPDesktop slug={slug} isEditOrder={isEditOrder} isOutOfStock={isOutOfStock} showOutOfStockNotification={showNotification} pdpIsModularBYOProduct={pdpIsModularBYOProduct} showDiscontinuedNotification={isDiscontinued} discontinuedCategoryMsg={isDiscontinued ? discontinuedCategoryMsg : null} productHeroImage={productHeroImage} productCylindoHeroImage={productCylindoHeroImage} />}</DesktopOnly>;
    $[25] = discontinuedCategoryMsg;
    $[26] = isDiscontinued;
    $[27] = isEditOrder;
    $[28] = isOutOfStock;
    $[29] = pdpIsModularBYOProduct;
    $[30] = productCylindoHeroImage;
    $[31] = productHeroImage;
    $[32] = showLayoutRevamp;
    $[33] = showNotification;
    $[34] = slug;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <PDPPageAnalytics />;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== showLayoutRevamp) {
    t16 = !showLayoutRevamp && <PDPFooter />;
    $[37] = showLayoutRevamp;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== t12 || $[40] !== t13 || $[41] !== t14 || $[42] !== t16) {
    t17 = <PDPStickyButtonsProvider>{t12}{t13}{t14}{t15}{t16}</PDPStickyButtonsProvider>;
    $[39] = t12;
    $[40] = t13;
    $[41] = t14;
    $[42] = t16;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== pdpContextValue || $[45] !== t17) {
    t18 = <PDPContext.Provider value={pdpContextValue}>{t17}</PDPContext.Provider>;
    $[44] = pdpContextValue;
    $[45] = t17;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  return t18;
};
PDPContextRegion.propTypes = {
  slugOverride: PropTypes.string,
  isEditOrder: PropTypes.bool,
  pdpIsModularBYOProduct: PropTypes.bool,
  productOptionsPromise: PropTypes.object,
  productBasicDetailsPromise: PropTypes.object,
  productHeroImageFromServer: PropTypes.shape({
    src: PropTypes.string.isRequired,
    width: PropTypes.number,
    height: PropTypes.number,
    alt: PropTypes.string
  }),
  productCylindoHeroImage: PropTypes.shape({
    src: PropTypes.string.isRequired,
    width: PropTypes.number,
    height: PropTypes.number,
    alt: PropTypes.string
  })
};
export default PDPContextRegion;